/* eslint-disable no-unused-vars */
import React from "react";
import { graphql } from "gatsby";
import { lazyBlock } from "../utils/lazy-block";
import { usePaginatedList } from "../utils/use-paginated-list";
import { getBlockAnchor } from "../utils/helpers";
import parse from "html-react-parser";
import PropTypes from "prop-types";
import Button from "./button";
import Card from "./card";
import Columns from "./columns";
import Column from "./column";
import Container from "./container";
import GutenbergBlocks from "./gutenberg-blocks";
import Hero from "./hero";
import Image from "./image";
import SectionTitle from "./sectiontitle";
import VideoCard from "./videocard";
import BlogCard from "./blogcard";
import Metric from "./metric";
import InfoCard from "./infocard";
import FaqWrapper from "./faqwrapper";
import Accordion from "./accordion";
import SuperHero from "./superhero";
import EmployeeCard from "./employee-card";
import InfoDivider from "./info-divider";
import CTACard from "./ctacard";
import { Cover } from "./cover";
import Heading from "./heading";
import Separator from "./separator";
import Paragraph from "./paragraph";
import FileDownload from "./filedownload";
import Navigation from "./navigation";
import BusCard from "./bus-card";
import { gql } from "@apollo/client";
import { backgroundColor, linkTextColor, textColor } from "../utils/color";
import MediaText from "./media-text";
import BusPriceExample from "./bus-price-example";
import BusPriceExampleWrapper from "./bus-price-example-wrapper";
import Reviews from "./reviews";
import BrandLogoImage from "./brand-logo-image";
import BrandLogo from "./brand-logo";

/* Blocks that are heavy (forms with phone input, calculators, shop, maps, masonry,
 * carousels of quotes) or rarely used are split out of the shared page chunk so the
 * pages that don't contain them don't pay for them. See utils/lazy-block.js. */
const ContactForm = lazyBlock("contact-form", () => import("./contact-form"));
const FormChooser = lazyBlock("form-chooser", () => import("./form-chooser"));
const Location = lazyBlock("location", () => import("./location"));
const MasonryImageGrid = lazyBlock(
  "masonry-image-grid",
  () => import("./masonryimagegrid")
);
const Quotes = lazyBlock("quotes", () => import("./quotes"));
const Cart = lazyBlock("cart", () => import("./cart"));
const Checkout = lazyBlock("checkout", () => import("./checkout"));
const FeaturedProduct = lazyBlock(
  "featured-product",
  () => import("./featured-product")
);
const UpdateUser = lazyBlock("update-user", () => import("./update-user"));
const CalculatorBig = lazyBlock(
  "calculator-big",
  () => import("./calculator-big")
);
const CalculatorSmall = lazyBlock(
  "calculator-small",
  () => import("./calculator-small")
);

const getBlockStyle = (block) => {
  if (!block?.attributes?.style) {
    return undefined;
  }

  try {
    return typeof block.attributes.style === "string"
      ? JSON.parse(block.attributes.style)
      : block.attributes.style;
  } catch {
    return undefined;
  }
};

const blockTextAlign = (block) =>
  getBlockStyle(block)?.typography?.textAlign ?? block.attributes?.align;

const textAlignStyle = (block) => {
  const textAlign = blockTextAlign(block);

  return textAlign ? { textAlign } : undefined;
};

export default function GutenbergBlock({ block, aboveFold, ...props }) {
  switch (block.name) {
    case "core/navigation":
      return (
        <Navigation
          align={block.attributes?.align}
          parsedFontSize={block.attributes?.parsedFontSize}
          textColor={block.attributes?.textColor}
          content={block.dynamicContent}
          className={block.attributes?.className}
        />
      );
    case "core/button":
      return (
        <Button
          variant={
            block.attributes.className === "is-style-outline"
              ? "outlined"
              : "filled"
          }
          label={block.attributes.text}
          target={block.attributes.linkTarget}
          rel={block.attributes.rel}
          url={block.attributes.url?.replace(process.env.GATSBY_SITE_URL, "")}
          innerClassName={block.attributes.className}
          backgroundColor={block.attributes?.backgroundColorHex}
          textColor={block.attributes?.textColorHex}
        />
      );

    case "woocommerce/cart":
      return <Cart name={block.name} />;

    case "core/buttons":
      return (
        <div
          className={`button-container ${block.attributes.className ?? ""}`}
          style={{
            justifyContent: block.attributes.contentJustification
              ? `${block.attributes.contentJustification}`
              : "flex-start",
          }}
        >
          <GutenbergBlocks blocks={block.innerBlocks} />
        </div>
      );

    case "core/block":
      return (
        <GutenbergBlocks
          blocks={block.attributes?.reusableBlock?.editorBlocks}
          isTopLevel={true}
          disableAboveFold={!aboveFold}
        />
      );

    case "core/columns":
      return (
        <Columns
          $backgroundColor={backgroundColor(block)}
          className={block.attributes.className}
          $linkColor={linkTextColor(block)}
          $noGap={block.attributes?.blockGap}
          $padding={block.attributes?.padding}
          $textColor={textColor(block)}
        >
          <GutenbergBlocks blocks={block.innerBlocks} />
        </Columns>
      );

    case "core/column":
      return (
        <Column
          backgroundColor={backgroundColor(block)}
          className={block.attributes.className}
          linkColor={linkTextColor(block)}
          textColor={textColor(block)}
          width={block.attributes.width}
          verticalAlignment={block.attributes.verticalAlignment || "top"}
          padding={block.attributes.padding}
        >
          <GutenbergBlocks blocks={block.innerBlocks} />
        </Column>
      );
    case "core/cover":
      return (
        <Cover
          minHeight={block.attributes?.minHeight}
          minHeightUnit={block.attributes?.minHeightUnit}
          align={block.attributes?.align}
          media={block.attributes?.media}
          dimRatio={block.attributes?.dimRatio}
          duotoneColorValues={
            block.attributes?.duotoneColorValues
              ? JSON.parse(block.attributes.duotoneColorValues)
              : undefined
          }
          overlayColor={block.attributes?.overlayColor}
          contentPosition={block.attributes?.contentPosition}
          hasParallax={block.attributes?.hasParallax}
          className={block.attributes?.className}
          aboveFold={aboveFold}
        >
          <GutenbergBlocks blocks={block.innerBlocks} />
        </Cover>
      );
    case "core/group":
      return (
        <GutenbergBlocks blocks={block.innerBlocks} aboveFold={aboveFold} />
      );

    case "core/image": {
      const maxWidth = ((size) => {
        switch (size) {
          case "full":
            return "xl";
          case "thumbnail":
            return "xs";
          case "medium":
            return "sm";
          case "large":
          default:
            return "md";
        }
      })(block.attributes.sizeSlug);
      return (
        <Container
          maxWidth={maxWidth}
          disableMargin={true}
          disableGutters={true}
          disableAnimation={aboveFold}
          className={block.attributes.className}
        >
          <Image
            image={block.attributes.image}
            className={`img-width ${block.attributes.className}`}
            alt={block.attributes.alt}
            loading={aboveFold ? "eager" : undefined}
            fetchpriority={aboveFold ? "high" : undefined}
          />
          {block.attributes.caption && (
            <p
              className="center"
              dangerouslySetInnerHTML={{ __html: block.attributes.caption }}
            />
          )}
        </Container>
      );
    }

    case "core/gallery":
      return <MasonryImageGrid photos={block.attributes.images} />;

    case "core/media-text": {
      return (
        <MediaText
          backgroundColor={backgroundColor(block)}
          imageFill={block.attributes.imageFill}
          isStackedOnMobile={block.attributes.isStackedOnMobile}
          linkColor={linkTextColor(block)}
          media={block.attributes.media}
          mediaPosition={block.attributes.mediaPosition}
          mediaWidth={block.attributes.mediaWidth}
          textColor={textColor(block)}
          className={block.attributes.className}
          aboveFold={aboveFold}
        >
          <GutenbergBlocks blocks={block.innerBlocks} />
        </MediaText>
      );
    }

    case "core/latest-posts": {
      const [postList, LoadMorePostsButton] = usePaginatedList(
        block.attributes.posts,
        8
      );
      return (
        <div className={`blog-container-${block.attributes.postLayout}`}>
          {postList.map((post, index) => (
            <BlogCard
              key={index}
              title={post.title}
              date={post.date}
              excerpt={post.excerpt}
              link={post.uri}
              image={post?.featuredImage?.node}
              author={post.author.node.name}
              displayAuthor={block.attributes.displayAuthor}
              displayFeaturedImage={block.attributes.displayFeaturedImage}
              displayPostContent={block.attributes.displayPostContent}
              displayPostDate={block.attributes.displayPostDate}
              columns={block.attributes.columns}
              layout={block.attributes.postLayout}
              excerptLength={block.attributes.excerptLength}
            />
          ))}
          <LoadMorePostsButton>Last in flere</LoadMorePostsButton>
        </div>
      );
    }

    case "core/paragraph":
      return (
        <Paragraph
          backgroundColor={backgroundColor(block)}
          color={textColor(block)}
          content={block?.attributes?.content}
          fontSize={block?.attributes?.parsedFontSize}
          linkColor={linkTextColor(block)}
          className={block?.attributes?.className}
          style={textAlignStyle(block)}
          anchor={getBlockAnchor(block)}
          direction={block?.attributes?.direction}
        />
      );
    case "core/heading":
      return (
        <Heading
          level={block.attributes?.level}
          anchor={getBlockAnchor(block)}
          className={block.attributes?.className}
          style={textAlignStyle(block)}
          margin={block.attributes?.margin}
          fontSize={block.attributes?.parsedFontSize}
          color={textColor(block)}
          backgroundColor={backgroundColor(block)}
          linkColor={linkTextColor(block)}
          content={block?.attributes?.content}
          textTransform={block?.attributes?.textTransform}
        />
      );
    case "core/separator":
      return (
        <Separator
          className={block.attributes?.className}
          color={block.attributes?.color}
          backgroundColor={block.attributes?.backgroundColor}
        />
      );
    case "core/file":
      return (
        <FileDownload
          fileName={block.attributes?.fileName}
          url={block.attributes?.href}
          showDownloadButton={block.attributes?.showDownloadButton}
          downloadButtonText={block.attributes?.downloadButtonText}
          mediaItem={block.attributes?.mediaItem}
        ></FileDownload>
      );
    case "mev/reviews":
      return <Reviews id={block.attributes.trustindexID} />;
    case "mev/updateuser":
      return <UpdateUser />;
    case "mev/brand-logo": {
      return (
        <BrandLogo
          title={block.attributes.title}
          numberOfColumns={block.attributes.numberOfColumns}
        >
          {block.innerBlocks.map((innerBlock, i) => (
            <GutenbergBlock key={i} block={innerBlock} />
          ))}
        </BrandLogo>
      );
    }

    case "mev/brand-logo-image": {
      return <BrandLogoImage image={block.attributes.image} />;
    }

    case "mev/form-chooser": {
      const {
        privateFormId,
        privateFormHtml,
        privateFormUrl,
        businessFormId,
        businessFormHtml,
        businessFormUrl,
        language,
      } = block.attributes;
      return (
        <FormChooser
          language={language}
          privateForm={{
            id: privateFormId,
            html: privateFormHtml,
            url: privateFormUrl,
          }}
          businessForm={{
            id: businessFormId,
            html: businessFormHtml,
            url: businessFormUrl,
          }}
        />
      );
    }

    case "mev/bus-card":
      return (
        <BusCard
          title={block.attributes?.bus?.title}
          uri={block.attributes?.bus?.uri}
          image={block.attributes?.bus?.featuredImage?.node}
          isBusinessClass={block.attributes?.bus?.crbBusinessClass}
          tags={block.attributes?.bus?.crbTags}
        />
      );
    case "mev/bus-price-example":
      return (
        <BusPriceExample
          title={block.attributes?.title}
          subTitle={block.attributes?.subTitle}
          image={block.attributes?.image}
          imageURL={block.attributes?.imageURL}
          pricerows={block.attributes?.pricerows}
          bus={block.attributes?.bus}
        />
      );
    case "mev/bus-price-example-wrapper":
      return (
        <BusPriceExampleWrapper bus={block.attributes?.bus}>
          {block.innerBlocks.map((block, i) => (
            <GutenbergBlock key={i} block={block} />
          ))}
        </BusPriceExampleWrapper>
      );
    case "mev/calculator-big":
      return (
        <CalculatorBig
          steps={block.innerBlocks.map((block) => ({
            label: block?.attributes?.title,
          }))}
        >
          {block.innerBlocks.map((block, i) => (
            <GutenbergBlock key={i} block={block} />
          ))}
        </CalculatorBig>
      );
    case "mev/calculator-small":
      return (
        <CalculatorSmall
          title={block.attributes?.title}
          buttonText={block.attributes?.buttonText}
          buttonUrl={block.attributes?.buttonUrl}
          className={block.attributes?.className}
        />
      );
    case "mev/card":
      return block.attributes?.variation === "cta" ? (
        <CTACard
          image={block.attributes.cardImage}
          label={block.attributes.urlLabel}
          linkUrl={
            block.attributes.postType === "page"
              ? block.attributes.page?.uri
              : block.attributes.postType === "post"
                ? block.attributes.post?.uri
                : block.attributes.url
          }
        />
      ) : (
        <Card
          title={block.attributes.title}
          image={block.attributes.cardImage}
          description={block.attributes.description}
          label={block.attributes.urlLabel}
          linkUrl={block.attributes.page?.uri || block.attributes.url}
          twoCol={block.attributes.twoCol}
          isSmall={block.attributes?.variation === "small"}
          textAlign={block.attributes?.textAlign}
        />
      );

    case "mev/container":
      return (
        <Container
          disableGutters={block.attributes.disableGutters}
          disableMargin={block.attributes.disableMargins}
          maxWidth={block.attributes.maxWidth}
          variant={block.attributes.variant}
          alignContent={block.attributes.alignContent}
          disableAnimation={block.attributes.disableAnimation || aboveFold}
          backgroundColor={backgroundColor(block)}
          textColor={textColor(block)}
          className={block.attributes?.className}
        >
          <GutenbergBlocks blocks={block.innerBlocks} aboveFold={aboveFold} />
        </Container>
      );

    case "mev/employee-card":
      return (
        <EmployeeCard
          title={block.attributes.title}
          image={block.attributes.image}
          name={block.attributes.name}
          city={block.attributes.city}
          email={block.attributes.email}
          phone={block.attributes.phone}
        />
      );

    case "mev/faq":
      return (
        <FaqWrapper>
          <Accordion
            answer={block.attributes.answer}
            question={block.attributes.question}
          />
        </FaqWrapper>
      );

    case "mev/info-card":
      return (
        <InfoCard
          title={block.attributes.title}
          text={block.attributes.text}
          icon={
            block.attributes.icon?.json
              ? JSON.parse(block.attributes?.icon?.json)
              : null
          }
          variant={block.attributes?.variant}
          alignContent={block.attributes?.alignContent}
          backgroundColorHex={block.attributes?.backgroundColorHex}
          textColorHex={block.attributes?.textColorHex}
          iconColorHex={block.attributes?.iconColorHex}
          className={block.attributes?.className}
        />
      );

    case "mev/info-divider":
      return (
        <InfoDivider image={block.attributes.image}>
          <GutenbergBlocks blocks={block.innerBlocks} />
        </InfoDivider>
      );

    case "mev/location": {
      return (
        <Location
          btnUrl={block.attributes.buttonURL}
          variant={block.attributes.variant}
          disableGutters={block.attributes.disableGutters}
          company={block.attributes.company}
          street={block.attributes.street}
          postal={block.attributes.postal}
          email={block.attributes.email}
          phone={block.attributes.phone}
          lat={Number(block.attributes.lat)}
          lng={Number(block.attributes.lng)}
          label={block.attributes.buttonLabel}
        >
          <GutenbergBlocks blocks={block.innerBlocks} />
        </Location>
      );
    }
    case "mev/metric":
      return (
        <Metric
          icon={
            block.attributes.icon?.json
              ? JSON.parse(block.attributes.icon?.json)
              : null
          }
          number={block.attributes.number}
          unit={block.attributes.unit}
          text={block.attributes.text}
        />
      );

    case "mev/hero":
      return (
        <Hero
          media={block.attributes?.media}
          mobileMedia={block.attributes?.mobileMedia}
          variant={block.attributes?.variant}
          mediaItems={block.attributes?.mediaItems}
          mediaItemsMobile={block.attributes?.mediaItemsMobile}
          backgroundImage={block.attributes?.backgroundImage}
        >
          <GutenbergBlocks blocks={block.innerBlocks} aboveFold={aboveFold} />
        </Hero>
      );

    case "mev/section-title":
      return (
        <SectionTitle
          title={block.attributes.title}
          position={
            block.attributes.mevAlign === "center" ? "centered" : "left"
          }
          linkLabel={block.attributes.urlLabel}
          linkUrl={block.attributes.page?.uri || block.attributes.url}
          textColor={block.attributes.mevTextColor}
        >
          <GutenbergBlocks blocks={block.innerBlocks} />
        </SectionTitle>
      );

    case "mev/superhero": {
      const slides = block.innerBlocks.reduce((slides, block) => {
        if (block.name === "mev/superhero-slide") {
          slides.push({
            image: block.attributes.image,
            children: block.innerBlocks,
          });
        }
        return slides;
      }, []);
      return <SuperHero autoplay={block.attributes.autoplay} slides={slides} />;
    }

    case "mev/video-card":
      return (
        <VideoCard
          title={block.attributes.title}
          date={block.attributes.date}
          description={block.attributes.description}
          youTubeID={block.attributes.youtubeId}
          vimeoID={block.attributes.vimeoId}
          videoUrl={block.attributes.videoURL}
          siteUrl={block.attributes.page?.uri || block.attributes.siteURL}
          urlLabel={block.attributes.urlLabel}
          image={block.attributes.image}
          videoSource={block.attributes.videoSource}
        />
      );

    case "mev/masonry-grid": {
      const photos = block.innerBlocks.reduce((photos, block) => {
        if (block.name === "mev/masonry-grid-image") {
          photos.push({ image: block.attributes.image });
        }
        return photos;
      }, []);
      return <MasonryImageGrid photos={photos} />;
    }

    case "mev/quotes": {
      const quotes = block.innerBlocks.reduce((quotes, block) => {
        if (block.name === "mev/quote") {
          quotes.push({
            title: block.attributes.title,
            quote: block.attributes.quote,
            author: block.attributes.author,
          });
        }
        return quotes;
      }, []);
      return <Quotes title={block.attributes.title} quotes={quotes} />;
    }

    case "contact-form-7/contact-form-selector":
      return (
        <ContactForm
          action={block.attributes.url}
          html={block.attributes.rendered}
          id={block.attributes.id}
        />
      );

    case "woocommerce/checkout":
      return <Checkout />;

    case "woocommerce/featured-product":
      return (
        <FeaturedProduct
          align={block.attributes?.align}
          className={block.attributes?.className}
          contentAlign={block.attributes?.contentAlign}
          overlayColor={block.attributes?.overlayColor}
          showDesc={block.attributes?.showDesc}
          showPrice={block.attributes?.showPrice}
          height={block.attributes?.height}
          product={block.attributes?.product}
          dimRatio={block.attributes?.dimRatio}
          media={block.attributes?.media || block.attributes?.product?.image}
        >
          <GutenbergBlocks blocks={block.innerBlocks} />
        </FeaturedProduct>
      );

    default:
      if (typeof block.renderedHtml === "string" && block.renderedHtml) {
        return parse(block.renderedHtml);
      } else {
        return <></>;
      }
  }
}

GutenbergBlock.propTypes = {
  block: PropTypes.object.isRequired,
  aboveFold: PropTypes.bool,
};

export const query = graphql`
  fragment GutenbergBlock on WpPageEditorBlock {
    name
    isDynamic
    renderedHtml
    ... on WpMevBrandLogo {
      attributes {
        title
        numberOfColumns
      }
    }

    ... on WpMevBrandLogoImage {
      attributes {
        image {
          altText
          mimeType
          sourceUrl
          focalPoint {
            objectPosition
          }
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
      }
    }
    ... on WpMevBusCard {
      attributes {
        bus {
          crbBusinessClass
          title
          uri
          featuredImage {
            node {
              altText
              sourceUrl
              focalPoint {
                objectPosition
              }
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    width: 760
                    sizes: "(max-width: 600px) 100vw, (max-width: 1025px) 50vw, 380px"
                  )
                }
              }
            }
          }
          crbTags {
            crb_tag_color {
              value
            }
            text
          }
        }
      }
    }

    ... on WpMevBusPriceExample {
      attributes {
        bus {
          crbChosenBusFeatures {
            label
            value
          }
        }
        pricerows
        title
        subTitle
        imageURL
        image {
          sourceUrl
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
      }
    }

    ... on WpMevCalculatorSmall {
      attributes {
        buttonText
        buttonUrl
        title
        className
      }
    }
    ... on WpMevCard {
      attributes {
        cardImage: image {
          altText
          sourceUrl
          focalPoint {
            objectPosition
          }
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
        imageWide: image {
          altText
          sourceUrl
          focalPoint {
            objectPosition
          }
          localFile {
            childImageSharp {
              gatsbyImageData(aspectRatio: 2, width: 1030)
            }
          }
        }
        page {
          uri
        }
        url
        title
        urlLabel
        description
        twoCol
        variation
        textAlign
        postType
      }
    }

    ... on WpMevContainer {
      attributes {
        backgroundColor
        disableGutters
        disableMargins
        maxWidth
        style
        textColor
        variant
        alignContent
        disableAnimation
        className
      }
    }

    ... on WpMevEmployeeCard {
      attributes {
        city
        email
        name
        phone
        title
        image {
          altText
          sourceUrl
          focalPoint {
            objectPosition
          }
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
      }
    }

    ... on WpMevFaq {
      attributes {
        answer
        question
      }
    }

    ... on WpMevInfoCard {
      attributes {
        title
        text
        icon {
          json
        }
        backgroundColorHex
        textColorHex
        iconColorHex
        variant
        alignContent
        className
      }
    }

    ... on WpMevInfoDivider {
      attributes {
        image {
          altText
          sourceUrl
          focalPoint {
            objectPosition
          }
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
      }
    }

    ... on WpMevLocation {
      attributes {
        buttonLabel
        buttonURL
        company
        disableGutters
        lat
        lng
        postal
        street
        variant
        email
        phone
      }
    }

    ... on WpMevMetric {
      attributes {
        icon {
          json
        }
        text
        unit
        number
      }
    }

    ... on WpMevHero {
      attributes {
        variant
        media {
          mimeType
          altText
          sourceUrl
          localFile {
            publicURL
            childImageSharp {
              gatsbyImageData(layout: FULL_WIDTH)
            }
          }
        }
        mobileMedia {
          mimeType
          altText
          sourceUrl
          localFile {
            publicURL
            childImageSharp {
              gatsbyImageData(layout: FULL_WIDTH)
            }
          }
        }
        backgroundImage {
          sourceUrl
          mediaItemUrl
          altText
          mimeType
          localFile {
            publicURL
            childImageSharp {
              gatsbyImageData(layout: FULL_WIDTH)
            }
          }
        }
        mediaItems {
          mimeType
          altText
          sourceUrl
          localFile {
            publicURL
            childImageSharp {
              gatsbyImageData(layout: FULL_WIDTH)
            }
          }
        }
        mediaItemsMobile {
          mimeType
          altText
          sourceUrl
          localFile {
            publicURL
            childImageSharp {
              gatsbyImageData(layout: FULL_WIDTH)
            }
          }
        }
      }
    }

    ... on WpMevMasonryGridImage {
      attributes {
        image {
          altText
          sourceUrl
          focalPoint {
            objectPosition
          }
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
      }
    }

    ... on WpMevFormChooser {
      attributes {
        privateFormId
        privateFormHtml
        privateFormUrl
        businessFormId
        businessFormHtml
        businessFormUrl
        language
      }
    }

    ... on WpMevReviews {
      attributes {
        trustindexID
      }
    }

    ... on WpMevSectionTitle {
      attributes {
        title
        mevAlign: align
        urlLabel
        url
        mevTextColor: textColor
        page {
          uri
        }
      }
    }

    ... on WpMevSuperhero {
      attributes {
        autoplay
      }
    }

    ... on WpMevSuperheroSlide {
      attributes {
        image {
          mediaItemUrl
          sourceUrl
          altText
          mimeType
          focalPoint {
            objectPosition
          }
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
      }
    }

    ... on WpMevVideoCard {
      attributes {
        description
        youtubeId
        vimeoId
        image {
          altText
          sourceUrl
          focalPoint {
            objectPosition
          }
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
        page {
          uri
        }
        siteURL
        title
        urlLabel
        videoURL
        videoSource
      }
    }

    ... on WpMevQuotes {
      attributes {
        title
      }
    }

    ... on WpMevQuote {
      attributes {
        title
        quote
        author
      }
    }

    ... on WpCoreButtons {
      attributes {
        contentJustification
        className
      }
    }

    ... on WpCoreButton {
      attributes {
        text
        className
        url
        linkTarget
        rel
        backgroundColorHex
        textColorHex
      }
    }

    ... on WpCoreColumns {
      attributes {
        backgroundColor
        className
        padding
        style
        textColor
        anchor
      }
    }

    ... on WpCoreColumn {
      attributes {
        backgroundColor
        className
        padding
        style
        textColor
        verticalAlignment
        width
        anchor
      }
    }

    ... on WpCoreImage {
      attributes {
        className
        image {
          altText
          sourceUrl
          focalPoint {
            objectPosition
          }
          localFile {
            childImageSharp {
              gatsbyImageData(
                width: 1600
                outputPixelDensities: [0.25, 0.5, 0.75, 1]
                sizes: "(min-width: 1180px) 1180px, 100vw"
              )
            }
          }
        }
        alt
        title
        caption
        sizeSlug
      }
    }

    ... on WpCoreGallery {
      attributes {
        images {
          image {
            altText
            sourceUrl
            focalPoint {
              objectPosition
            }
            localFile {
              childImageSharp {
                gatsbyImageData
              }
            }
          }
        }
      }
    }

    ... on WpCoreMediaText {
      attributes {
        backgroundColor
        className
        imageFill
        isStackedOnMobile
        mediaPosition
        mediaWidth
        media {
          mimeType
          altText
          sourceUrl
          mediaItemUrl
          focalPoint {
            objectPosition
          }
          localFile {
            publicURL
            childImageSharp {
              gatsbyImageData(
                width: 1200
                outputPixelDensities: [0.25, 0.4, 0.5, 0.75, 1]
                sizes: "(max-width: 850px) 100vw, 600px"
              )
            }
          }
        }
        style
        textColor
      }
    }

    ... on WpContactForm7ContactFormSelector {
      attributes {
        id
        rendered
        url
      }
    }

    ... on WpCoreLatestPosts {
      attributes {
        columns
        postLayout
        posts {
          date(formatString: "DD.MM.YYYY")
          excerpt
          title
          uri
          featuredImage {
            node {
              altText
              sourceUrl
              focalPoint {
                objectPosition
              }
              localFile {
                childImageSharp {
                  gatsbyImageData
                }
              }
            }
          }
          author {
            node {
              name
            }
          }
        }
        displayFeaturedImage
        displayPostContent
        displayAuthor
        displayPostDate
        excerptLength
      }
    }

    ... on WpCoreParagraph {
      attributes {
        backgroundColor
        content
        align
        anchor
        className
        parsedFontSize
        direction
        style
        textColor
      }
    }
    ... on WpCoreHeading {
      attributes {
        anchor
        backgroundColor
        content
        align
        textTransform
        className
        parsedFontSize
        level
        margin
        style
        textColor
      }
    }
    ... on WpCoreFile {
      attributes {
        fileName
        href
        downloadButtonText
        showDownloadButton
        mediaItem {
          archiveContents {
            name
            size
            url
          }
        }
      }
    }
    ... on WpWoocommerceCart {
      name
    }
    ... on WpWoocommerceFeaturedProduct {
      attributes {
        align
        className
        contentAlign
        dimRatio
        height
        overlayColor
        showDesc
        showPrice
        media {
          mimeType
          altText
          sourceUrl
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
          focalPoint {
            objectPosition
          }
        }
        product {
          description
          name
          link
          image {
            mimeType
            altText
            sourceUrl
            localFile {
              childImageSharp {
                gatsbyImageData
              }
            }
          }
          ...ProductTypes
        }
      }
    }
    ... on WpCoreCover {
      attributes {
        align
        overlayColor
        dimRatio
        duotoneColorValues
        media {
          mimeType
          altText
          sourceUrl
          focalPoint {
            objectPosition
          }
          localFile {
            publicURL
            childImageSharp {
              gatsbyImageData(layout: FULL_WIDTH)
            }
          }
        }
        minHeight
        minHeightUnit
        contentPosition
        hasParallax
        className
      }
    }

    ... on WpCoreSeparator {
      attributes {
        className
        color: textColor
        backgroundColor
      }
    }
    ... on WpCoreNavigation {
      dynamicContent
      attributes {
        align
        backgroundColor
        style
        textColor
        parsedFontSize
        className
      }
    }
  }

  fragment ProductTypes on WpProduct {
    ... on WpVariableProduct {
      price
      name
      salePrice
    }
    ... on WpExternalProduct {
      name
      price
      salePrice
    }
    ... on WpGroupProduct {
      name
      price
    }
    ... on WpSimpleProduct {
      name
      price
      regularPrice
    }
  }

  fragment BusUpgrade on WpBus {
    ... on WpBus {
      crb3dLink
      crbBrand
      crbBusinessClass
      crbCalcText
      crbCalcImages {
        sourceUrl
        localFile {
          childImageSharp {
            gatsbyImageData
          }
        }
      }
      crbChosenBusFeatures {
        id
        label
        value
      }
      featuredImage {
        node {
          sourceUrl
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
      }
      id
      crbTags {
        crb_tag_color {
          value
        }
        text
      }
      title
    }
  }

  fragment Bus on WpBus {
    ... on WpBus {
      crbSuggestedBusUpgrade {
        ...BusUpgrade
        crbSuggestedBusUpgrade {
          ...BusUpgrade
          crbSuggestedBusUpgrade {
            ...BusUpgrade
            crbSuggestedBusUpgrade {
              ...BusUpgrade
              crbSuggestedBusUpgrade {
                ...BusUpgrade
              }
            }
          }
        }
      }
      featuredImage {
        node {
          sourceUrl
          focalPoint {
            objectPosition
          }
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
      }
      crbCalcText
      crbCalcImages {
        sourceUrl
        localFile {
          childImageSharp {
            gatsbyImageData
          }
        }
      }
      crbTags {
        crb_tag_color {
          value
        }
        text
      }
      crbBusinessClass
      crb3dLink
      crbChosenBusFeatures {
        id
        label
        value
      }
      id
      title
      order: menuOrder
    }
  }

  fragment ReusableBlock on WpPageEditorBlock {
    ... on WpCoreBlock {
      attributes {
        ref
        reusableBlock {
          editorBlocks {
            ...GutenbergBlock
            innerBlocks {
              ...GutenbergBlock
              innerBlocks {
                ...GutenbergBlock
              }
            }
          }
        }
      }
    }
  }

  fragment GutenbergBlocks on WpPageEditorBlock {
    ...GutenbergBlock
    ...ReusableBlock
    clientId
    parentClientId
  }
`;
GutenbergBlock.privateQuery = gql`
  fragment GutenbergBlock on PageEditorBlock {
    name
    isDynamic
    renderedHtml

    ... on CoreButtons {
      attributes {
        contentJustification
      }
    }

    ... on CoreButton {
      attributes {
        text
        className
        url
        linkTarget
        rel
      }
    }

    ... on CoreColumns {
      attributes {
        className
        padding
        anchor
      }
    }

    ... on CoreColumn {
      attributes {
        backgroundColor
        className
        style
        textColor
        verticalAlignment
        width
        padding
        anchor
      }
    }

    ... on CoreFile {
      attributes {
        fileName
        href
        showDownloadButton
        downloadButtonText
        mediaItem {
          archiveContents {
            name
            size
            url
          }
        }
      }
    }

    ... on CoreNavigation {
      dynamicContent
      attributes {
        align
        backgroundColor
        style
        textColor
        parsedFontSize
        className
      }
    }

    ... on CoreParagraph {
      attributes {
        backgroundColor
        content
        align
        anchor
        className
        parsedFontSize
        direction
        style
        textColor
      }
    }

    ... on CoreHeading {
      attributes {
        anchor
        backgroundColor
        content
        align
        textTransform
        className
        parsedFontSize
        level
        margin
        style
        textColor
      }
    }

    ... on MevContainer {
      attributes {
        backgroundColor
        disableGutters
        disableMargins
        maxWidth
        style
        textColor
        variant
        alignContent
        disableAnimation
        className
      }
    }
  }

  fragment ReusableBlock on WpPageEditorBlock {
    ... on WpCoreBlock {
      attributes {
        ref
        reusableBlock {
          editorBlocks {
            ...GutenbergBlock
            innerBlocks {
              ...GutenbergBlock
              innerBlocks {
                ...GutenbergBlock
                innerBlocks {
                  ...GutenbergBlock
                  innerBlocks {
                    ...GutenbergBlock
                    innerBlocks {
                      ...GutenbergBlock
                      innerBlocks {
                        ...GutenbergBlock
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  fragment GutenbergBlocks on WpPageEditorBlock {
    ...GutenbergBlock
    ...ReusableBlock
    clientId
    parentClientId
    innerBlocks {
      ...GutenbergBlock
      ...ReusableBlock
      innerBlocks {
        ...GutenbergBlock
        ...ReusableBlock
        innerBlocks {
          ...GutenbergBlock
          ...ReusableBlock
          innerBlocks {
            ...GutenbergBlock
            ...ReusableBlock
            innerBlocks {
              ...GutenbergBlock
              ...ReusableBlock
            }
          }
        }
      }
    }
  }
`;
